import React, { useState, useEffect } from 'react';
import { X, Plus, Minus, Trash2, ShoppingBag, CreditCard } from 'lucide-react';
import { useCart } from '../context/CartContext';
import stripeService from '../services/stripeService';
import { useProducts } from '../context/ProductsContext';
import './Cart.css';

const Cart = ({ isOpen, onClose }) => {
  const { items, updateQuantity, removeFromCart, clearCart, getTotalPrice } = useCart();
  const { products } = useProducts();
  const [isClosing, setIsClosing] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // Gérer l'animation de fermeture
  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300); // Durée de l'animation de fermeture
  };

  // Réinitialiser l'état de fermeture quand le panier s'ouvre
  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
    }
  }, [isOpen]);

  if (!isOpen && !isClosing) return null;

  const handleQuantityChange = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
    } else {
      updateQuantity(productId, newQuantity);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const handleOverlayClick = (e) => {
    // Fermer le panier seulement si on clique sur l'overlay (pas sur la sidebar)
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Gérer le paiement avec Stripe
  const handleStripeCheckout = async () => {
    setIsProcessing(true);
    console.log('🚀 DÉBUT DU PROCESSUS DE PAIEMENT');
    console.log('📦 Articles dans le panier:', items.length);
    
    try {
      // Vérifier que le panier n'est pas vide
      if (items.length === 0) {
        throw new Error('Votre panier est vide. Ajoutez des produits avant de payer.');
      }

      // Créer un Map pour stocker les IDs Stripe synchronisés
      const stripeIdsMap = new Map();
      
      // Vérifier si des produits n'ont pas d'IDs Stripe et les synchroniser si nécessaire
      const itemsWithoutStripeIds = items.filter(item => !item.stripeProductId || !item.stripePriceId);
      
      if (itemsWithoutStripeIds.length > 0) {
        console.log(`🔄 ${itemsWithoutStripeIds.length} produit(s) sans IDs Stripe, synchronisation en cours...`);
        setIsSyncing(true);
        
        try {
          // Récupérer les produits complets depuis le contexte (qui ont peut-être été synchronisés)
          const productsToSync = itemsWithoutStripeIds.map(cartItem => {
            // Chercher le produit dans le contexte avec les IDs Stripe à jour
            const fullProduct = products.find(p => p.id === cartItem.id) || cartItem;
            return fullProduct;
          });
          
          // Synchroniser rapidement les produits manquants
          const syncResult = await stripeService.syncProducts(productsToSync);
          
          if (syncResult.syncedProducts && syncResult.syncedProducts.length > 0) {
            console.log(`✅ ${syncResult.syncedProducts.length} produit(s) synchronisé(s) avec Stripe`);
            
            // Stocker les IDs Stripe dans un Map pour les utiliser lors de la création de cartItems
            syncResult.syncedProducts.forEach(syncedProduct => {
              stripeIdsMap.set(syncedProduct.id, {
                stripeProductId: syncedProduct.stripeProductId,
                stripePriceId: syncedProduct.stripePriceId
              });
            });
          }
        } catch (syncError) {
          console.warn('⚠️ Erreur lors de la synchronisation pré-paiement:', syncError.message);
          console.info('💡 Le paiement continuera, les produits seront créés sur Stripe à la volée.');
        } finally {
          setIsSyncing(false);
        }
      }

      // Préparer les articles du panier pour Stripe
      // Inclure les IDs Stripe si disponibles pour un paiement plus rapide et sécurisé
      const cartItems = items.map(item => {
        // Récupérer les IDs Stripe : depuis l'item, depuis le Map de synchronisation, ou depuis le contexte
        const syncedIds = stripeIdsMap.get(item.id);
        const contextProduct = products.find(p => p.id === item.id);
        
        const cartItem = {
          product: {
            id: item.id,
            name: item.name || 'Produit sans nom',
            description: item.description || `${item.category || 'Non catégorisé'} - ${item.unit || 'unité'}`,
            price: item.price || 0,
            category: item.category || 'Autre',
            unit: item.unit || 'unité',
            image: item.image || '/images/beef-steak.svg',
          },
          quantity: item.quantity || 1
        };

        // Inclure les IDs Stripe dans l'ordre de priorité : synchronisation récente > contexte > item
        const stripeProductId = syncedIds?.stripeProductId || contextProduct?.stripeProductId || item.stripeProductId;
        const stripePriceId = syncedIds?.stripePriceId || contextProduct?.stripePriceId || item.stripePriceId;
        
        if (stripeProductId) {
          cartItem.product.stripeProductId = stripeProductId;
        }
        if (stripePriceId) {
          cartItem.product.stripePriceId = stripePriceId;
        }

        return cartItem;
      });

      console.log('💳 Préparation de la session de paiement Stripe...');
      console.log('📋 Articles à payer:', JSON.stringify(cartItems, null, 2));
      
      // Créer une session de paiement (timeout augmenté à 60 secondes pour Stripe)
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout: Le serveur met trop de temps à répondre. Vérifiez que le serveur backend est démarré (npm run server).')), 60000)
      );

      const sessionPromise = stripeService.createCheckoutSession(cartItems);
      
      const { url, sessionId } = await Promise.race([sessionPromise, timeoutPromise]);
      
      if (!url || !sessionId) {
        throw new Error('Réponse invalide du serveur: URL ou sessionId manquant');
      }

      console.log('✅ Session créée avec succès:', sessionId);
      console.log('🔗 Redirection vers:', url);
      
      // Sauvegarder la page actuelle avant de rediriger vers Stripe
      const currentPath = window.location.pathname;
      sessionStorage.setItem('stripe-return-page', currentPath);
      console.log('💾 Page sauvegardée pour retour:', currentPath);
      
      // Sauvegarder les items du panier pour réduire le stock après paiement
      sessionStorage.setItem('stripe-purchase-items', JSON.stringify(items));
      console.log('💾 Items du panier sauvegardés pour réduction du stock:', items.length);
      
      // Rediriger vers la page de paiement Stripe
      window.location.href = url;
    } catch (error) {
      console.error('═'.repeat(60));
      console.error('❌ ERREUR lors du paiement:');
      console.error('   - Type:', error.name);
      console.error('   - Message:', error.message);
      console.error('   - Stack:', error.stack);
      console.error('═'.repeat(60));
      
      // Message d'erreur utilisateur
      let userMessage = error.message || 'Une erreur est survenue lors du paiement.';
      
      if (error.message && error.message.includes('Timeout')) {
        userMessage = 'Le serveur met trop de temps à répondre.\n\nVérifiez que le serveur backend est démarré:\n  npm run server';
      } else if (error.message && error.message.includes('Failed to fetch')) {
        userMessage = 'Impossible de contacter le serveur de paiement.\n\nVérifiez que:\n- Le serveur backend est démarré (npm run server)\n- Le serveur écoute sur le port 4242';
      }
      
      alert(`❌ Erreur lors du paiement:\n\n${userMessage}\n\nConsultez la console (F12) pour plus de détails.`);
    } finally {
      setIsProcessing(false);
      console.log('🏁 PROCESSUS DE PAIEMENT TERMINÉ');
    }
  };

  return (
    <div className={`cart-overlay ${isClosing ? 'closing' : ''}`} onClick={handleOverlayClick}>
      <div className={`cart-sidebar ${isClosing ? 'closing' : ''}`}>
        <div className="cart-header">
          <div className="cart-title">
            <ShoppingBag size={24} className="shopping-bag-icon" />
            Mon Panier
          </div>
          <button className="cart-close" onClick={handleClose} aria-label="Fermer le panier">
            <X size={22} />
          </button>
        </div>

        <div className="cart-content">
          {items.length === 0 ? (
            <div className="cart-empty">
              <ShoppingBag size={80} className="cart-empty-icon" />
              <h3>Votre panier est vide</h3>
              <p>Découvrez nos produits et ajoutez-les à votre panier</p>
            </div>
          ) : (
            <>
              <div className="cart-items">
                {items.map((item) => (
                  <div key={item.id} className="cart-item">
                    <div className="cart-item-content">
                      <img src={item.image} alt={`${item.name} - ${item.category} bio dans le panier`} className="cart-item-image" loading="lazy" />
                      
                      <div className="cart-item-details">
                        <h4 className="cart-item-name">{item.name}</h4>
                        <span className="cart-item-category">{item.category}</span>
                        <p className="cart-item-price">
                          {formatPrice(item.price)} / {item.unit}
                        </p>
                        <p className="cart-item-subtotal">
                          Total: {formatPrice(item.price * item.quantity)}
                        </p>
                      </div>
                    </div>

                    <div className="cart-item-controls">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleQuantityChange(item.id, item.quantity - 1);
                        }}
                        className="quantity-btn"
                        type="button"
                      >
                        <Minus size={16} />
                      </button>
                      <span className="quantity-display">{item.quantity}</span>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleQuantityChange(item.id, item.quantity + 1);
                        }}
                        className="quantity-btn"
                        type="button"
                      >
                        <Plus size={16} />
                      </button>
                      
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFromCart(item.id);
                        }}
                        className="remove-btn"
                        type="button"
                        title="Supprimer du panier"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="cart-footer">
                <div className="cart-summary">
                  <div className="cart-total">
                    <strong>Total: {formatPrice(getTotalPrice())}</strong>
                  </div>
                  
                  <div className="cart-actions">
                    <button 
                      onClick={clearCart}
                      className="btn-secondary"
                      disabled={isProcessing}
                    >
                      Vider le panier
                    </button>
                    
                    <button 
                      className="btn-primary"
                      onClick={handleStripeCheckout}
                      disabled={isProcessing || isSyncing}
                    >
                      {isSyncing ? (
                        <>
                          <span>Synchronisation...</span>
                        </>
                      ) : isProcessing ? (
                        <>
                          <span>Chargement...</span>
                        </>
                      ) : (
                        <>
                          <CreditCard size={18} />
                          <span>Payer avec Stripe</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Cart;